// Full updated CarPage.jsx with image upload in notes
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  addDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import Spinner from '../components/Spinner';

export default function CarPage() {
  const { carId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [currentRental, setCurrentRental] = useState(null);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRentalId, setExpandedRentalId] = useState(null);
  const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteImage, setNoteImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchCarAndRentals = async () => {
      try {
        const carRef = doc(db, 'cars', carId);
        const carSnap = await getDoc(carRef);
        if (carSnap.exists()) setCar({ id: carSnap.id, ...carSnap.data() });

        const activeRentalQuery = query(
          collection(db, 'rentals'),
          where('carId', '==', carId),
          where('status', '==', 'active')
        );
        const activeSnap = await getDocs(activeRentalQuery);
        if (!activeSnap.empty) {
          const docSnap = activeSnap.docs[0];
          setCurrentRental({ id: docSnap.id, ...docSnap.data() });
        }

        const historyQuery = query(collection(db, 'rentals'), where('carId', '==', carId));
        const historySnap = await getDocs(historyQuery);
        const historyList = historySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRentalHistory(historyList);

        const notesRef = collection(db, `cars/${carId}/notes`);
        const notesSnap = await getDocs(notesRef);
        setNotes(notesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Error fetching car or rentals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCarAndRentals();
  }, [carId]);

  const handleReturnCar = async () => {
    if (!returnDate || !returnTime) {
      alert('Please select both return date and time.');
      return;
    }

    if (!window.confirm('Are you sure you want to return this car?')) return;

    try {
      const fullReturnDateTime = new Date(`${returnDate}T${returnTime}`);
      await updateDoc(doc(db, 'rentals', currentRental.id), {
        status: 'completed',
        returnDate: fullReturnDateTime
      });
      await updateDoc(doc(db, 'cars', carId), { status: 'available' });
      window.location.reload();
    } catch (err) {
      console.error('Error returning car:', err);
      alert('Failed to return car.');
    }
  };

  const addNote = async () => {
    if (!newNote.trim() && !noteImage) return;
    try {
      let imageUrl = '';
      if (noteImage) {
        const imageRef = ref(storage, `notes/${carId}-${Date.now()}-${noteImage.name}`);
        await uploadBytes(imageRef, noteImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      const noteRef = await addDoc(collection(db, `cars/${carId}/notes`), {
        text: newNote,
        imageUrl,
        createdAt: Timestamp.now()
      });

      setNotes([...notes, { id: noteRef.id, text: newNote, imageUrl }]);
      setNewNote('');
      setNoteImage(null);
    } catch (err) {
      console.error('Failed to add note:', err);
      alert('Failed to add note.');
    }
  };

  const nextImage = () => {
    if (!car?.imageUrls?.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % car.imageUrls.length);
  };

  const prevImage = () => {
    if (!car?.imageUrls?.length) return;
    setCurrentImageIndex((prev) => (prev - 1 + car.imageUrls.length) % car.imageUrls.length);
  };

  if (loading) return <Spinner />;
  if (!car) return <p style={loadingStyle}>Car not found.</p>;

  return (
    <div style={pageWrapper}>
      <div style={formCard}>
        <div style={formHeader}>
          <h2 style={formTitle}>{car.make} {car.model}</h2>
          <button onClick={() => navigate('/')} style={backButton}>← Back</button>
        </div>

        {car.imageUrls?.length > 0 && (
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <img src={car.imageUrls[currentImageIndex]} alt="Car" style={imageStyle} />
            {car.imageUrls.length > 1 && (
              <>
                <button onClick={prevImage} style={{ ...navArrow, left: '10px' }}>‹</button>
                <button onClick={nextImage} style={{ ...navArrow, right: '10px' }}>›</button>
              </>
            )}
          </div>
        )}

        <p><strong>Registration:</strong> {car.rego}</p>
        <p><strong>Color:</strong> {car.carColor || car.color || 'N/A'}</p>
        <p><strong>Year:</strong> {car.carYear || car.year || 'N/A'}</p>
        <p><strong>Status:</strong> <span style={{ color: car.status === 'available' ? '#10b981' : '#ef4444' }}>{car.status}</span></p>

        {/* Notes Section */}
        <div style={sectionBox}>
          <h3>📝 Notes</h3>
          {notes.map((note) => (
            <div key={note.id} style={{ marginBottom: '16px' }}>
              <p>• {note.text}</p>
              {note.imageUrl && (
                <img src={note.imageUrl} alt="Note Attachment" style={{ width: '100%', maxWidth: '300px', borderRadius: '8px', border: '1px solid #ddd', marginTop: '6px' }} />
              )}
            </div>
          ))}
          <div style={{ marginTop: '10px' }}>
            <textarea
              placeholder="Add a note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ccc', resize: 'none' }}
              rows={2}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNoteImage(e.target.files[0])}
              style={{ marginTop: '10px' }}
            />
            <button onClick={addNote} style={{ ...returnButton, marginTop: '10px' }}>Add Note</button>
          </div>
        </div>

        {currentRental && (
          <div style={sectionBox}>
            <h3>🔐 Currently Rented</h3>
            <p><strong>Name:</strong> {currentRental.customerName}</p>
            <p><strong>Phone:</strong> {currentRental.customerPhone}</p>
            <p><strong>Address:</strong> {currentRental.customerAddress}</p>
            <p><strong>Rented On:</strong> {new Date(currentRental.startDate.seconds * 1000).toLocaleString([], {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            <p><strong>Expected Return Date:</strong> {currentRental.endDate && currentRental.endDate.seconds ? new Date(currentRental.endDate.seconds * 1000).toLocaleDateString() : (currentRental.endDate || 'N/A')}</p>
            <p><strong>Weekly Rate:</strong> {currentRental.weeklyRate ? `$${currentRental.weeklyRate}` : 'N/A'}</p>
            <p><strong>Security Bond:</strong> {currentRental.securityDeposit ? `$${currentRental.securityDeposit}` : 'N/A'}</p>
            <p><strong>Driver's License Number:</strong> {currentRental.licenseNumber || 'N/A'}</p>
            <p><strong>Vehicle Color:</strong> {car.carColor || car.color || 'N/A'}</p>
            <p><strong>Vehicle Year:</strong> {car.carYear || car.year || 'N/A'}</p>

            {currentRental.licenseFrontImageURL && (
              <>
                <p><strong>License (Front):</strong></p>
                <img src={currentRental.licenseFrontImageURL} alt="License Front" style={licenseImageStyle} />
              </>
            )}
            {currentRental.licenseBackImageURL && (
              <>
                <p><strong>License (Back):</strong></p>
                <img src={currentRental.licenseBackImageURL} alt="License Back" style={licenseImageStyle} />
              </>
            )}

            {!showReturnDatePicker ? (
              <button onClick={() => setShowReturnDatePicker(true)} style={returnButton}>Return Car</button>
            ) : (
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                  <label style={{ fontWeight: 500 }}>
                    Return Date:
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      style={{
                        marginLeft: '10px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </label>
                  <label style={{ fontWeight: 500 }}>
                    Time:
                    <input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      style={{
                        marginLeft: '10px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        backgroundColor: '#f9fafb',
                        fontSize: '14px'
                      }}
                    />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleReturnCar} style={returnButton}>Confirm Return</button>
                  <button onClick={() => setShowReturnDatePicker(false)} style={cancelButton}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '30px' }}>
          <h3>📋 Rental History</h3>
          {rentalHistory.length === 0 ? (
            <p style={{ fontStyle: 'italic' }}>No rental history.</p>
          ) : (
            rentalHistory.map(rental => (
              <div key={rental.id} style={historyItem}>
                <div style={historyHeader}>
                  <div>
                    <strong>{rental.customerName}</strong> ({rental.status})<br />
                    Rented on: {new Date(rental.startDate.seconds * 1000).toLocaleString([], {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                  </div>
                  <button
                    onClick={() =>
                      setExpandedRentalId(expandedRentalId === rental.id ? null : rental.id)
                    }
                    style={toggleButton}
                  >
                    {expandedRentalId === rental.id ? 'Hide Details' : 'View Details'}
                  </button>
                </div>
                {expandedRentalId === rental.id && (
                  <div style={{ marginTop: '10px' }}>
                    <p><strong>Email:</strong> {rental.customerEmail}</p>
                    <p><strong>Phone:</strong> {rental.customerPhone}</p>
                    <p><strong>Address:</strong> {rental.customerAddress}</p>
                    <p><strong>Rented On:</strong> {new Date(rental.startDate.seconds * 1000).toLocaleString([], {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</p>
                    <p><strong>{rental.status === 'active' ? 'Expected Return Date' : 'Returned Date'}:</strong> {rental.status === 'active' ? (rental.endDate && rental.endDate.seconds ? new Date(rental.endDate.seconds * 1000).toLocaleDateString() : (rental.endDate || 'N/A')) : (rental.returnDate && rental.returnDate.seconds ? new Date(rental.returnDate.seconds * 1000).toLocaleString([], {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : (rental.status === 'completed' ? 'Returned (date not recorded)' : (rental.endDate && rental.endDate.seconds ? new Date(rental.endDate.seconds * 1000).toLocaleDateString() : (rental.endDate || 'N/A'))))}</p>
                    <p><strong>Weekly Rate:</strong> {rental.weeklyRate ? `$${rental.weeklyRate}` : 'N/A'}</p>
                    <p><strong>Security Bond:</strong> {rental.securityDeposit ? `$${rental.securityDeposit}` : 'N/A'}</p>
                    <p><strong>Driver's License Number:</strong> {rental.licenseNumber || 'N/A'}</p>
                    {rental.licenseFrontImageURL && (
                      <>
                        <p><strong>License Front:</strong></p>
                        <img src={rental.licenseFrontImageURL} alt="License Front" style={licenseImageStyle} />
                      </>
                    )}
                    {rental.licenseBackImageURL && (
                      <>
                        <p><strong>License Back:</strong></p>
                        <img src={rental.licenseBackImageURL} alt="License Back" style={licenseImageStyle} />
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


// Your original styling preserved below
const pageWrapper = {
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  padding: '40px 20px',
};

const formCard = {
  backgroundColor: '#fff',
  borderRadius: '16px',
  padding: '32px',
  width: '100%',
  maxWidth: '700px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
};

const formHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
};

const formTitle = {
  fontSize: '22px',
  fontWeight: 600,
  color: '#111827',
};

const backButton = {
  padding: '6px 12px',
  fontSize: '14px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: '#e5e7eb',
  color: '#374151',
  fontWeight: 500,
  cursor: 'pointer',
};

const imageStyle = {
  width: '100%',
  height: '250px',
  objectFit: 'cover',
  borderRadius: '10px',
};

const navArrow = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: '#ffffffcc',
  border: 'none',
  fontSize: '22px',
  padding: '8px 12px',
  borderRadius: '50%',
  cursor: 'pointer',
  zIndex: 2,
};

const licenseImageStyle = {
  width: '100%',
  maxWidth: '300px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  marginTop: '8px'
};

const sectionBox = {
  marginTop: '24px',
  padding: '16px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
};

const returnButton = {
  marginTop: '15px',
  padding: '10px 16px',
  backgroundColor: '#ef4444',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
};

const cancelButton = {
  marginTop: '15px',
  padding: '10px 16px',
  backgroundColor: '#e5e7eb',
  color: '#374151',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
};

const historyItem = {
  marginBottom: '16px',
  padding: '16px',
  backgroundColor: '#f9fafb',
  borderRadius: '10px',
  border: '1px solid #e5e7eb',
};

const historyHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const toggleButton = {
  backgroundColor: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  cursor: 'pointer',
};

const loadingStyle = {
  padding: '40px',
  textAlign: 'center',
  color: '#6b7280',
};
